import { useState } from 'react'
import { X } from 'lucide-react'
import { PROJECT_INITIAL_STATUSES, PROJECT_TYPES, type ProjectStatus } from '@symphenia/shared-types'
import { fieldLabel, fieldWrapper, inputBase, selectBase } from '@/lib/form-styles'
import { apiErrorText } from '@/lib/api-error'
import { useTerminology } from '@/lib/useTerminology'
import { createProject } from '../api/projects-head.api'
import { PROJECT_STATUS_LABELS, PROJECT_TYPE_LABELS } from '../project-labels'
import type { ProjectDetail } from '../types/project.types'
import { KundenSuche } from './auswahl'

/**
 * AP-35 Detailblatt 1 5.1 — **Projekt anlegen.**
 *
 * Seit AP-36 F (4.2) trägt der Dialog nur noch, was zum Anlegen nötig ist oder
 * danach nicht mehr frei wählbar ist: Typ, Anfangsstatus, Bezeichnung, Kunde.
 * Alles Übrige — Ansprechpartner, Betriebsstätte, Währung, Zuständigkeiten,
 * Termine, Kundenauftrag, Beschreibung — steht danach im Reiter „Projektdaten"
 * und speichert dort feldweise. Ein zweiter Ort für dieselben Felder hieße zwei
 * Prüfungen, die auseinanderlaufen.
 *
 * Der **Anfangsstatus** bleibt hier: Später ist der Status ein Wechsel mit
 * eigener Prüfung (Fall 3), kein Feld.
 */
export function ProjectFormDialog({ onClose, onSaved }: { onClose: () => void; onSaved: (p: ProjectDetail) => void }) {
  const terms = useTerminology()

  const [form, setForm] = useState({ type: 'ORDER', name: '', initialStatus: 'ACTIVE' as ProjectStatus })
  const [kunde, setKunde] = useState<{ id: string; name: string } | null>(null)
  const [speichert, setSpeichert] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)

  const kundePflicht = form.type !== 'INTERNAL'
  const setzen = (feld: keyof typeof form, wert: string) => setForm((f) => ({ ...f, [feld]: wert }))

  async function anlegen() {
    setSpeichert(true)
    setFehler(null)
    try {
      onSaved(
        await createProject({
          type: form.type,
          name: form.name.trim(),
          clientId: kunde?.id,
          initialStatus: form.initialStatus,
          createFirstPosition: false,
        }),
      )
    } catch (e) {
      setFehler(apiErrorText(e, 'Das hat nicht geklappt.'))
    } finally {
      setSpeichert(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4">
      <div className="w-full max-w-xl rounded-lg border border-border bg-background p-6 shadow-xl">
        <div className="mb-4 flex items-start justify-between">
          <h2 className="text-lg font-semibold text-primary">{terms.project} anlegen</h2>
          <button type="button" onClick={onClose} className="text-muted-foreground hover:text-foreground" aria-label="Schließen">
            <X className="h-5 w-5" />
          </button>
        </div>

        {fehler && <div className="mb-4 rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">{fehler}</div>}

        <div className="grid gap-4 sm:grid-cols-2">
          <div className={fieldWrapper}>
            <label className={fieldLabel}>Typ</label>
            <select className={selectBase} value={form.type} onChange={(e) => setzen('type', e.target.value)}>
              {PROJECT_TYPES.map((t) => (
                <option key={t} value={t}>
                  {PROJECT_TYPE_LABELS[t]?.(terms) ?? t}
                </option>
              ))}
            </select>
          </div>

          <div className={fieldWrapper}>
            <label className={fieldLabel}>Status</label>
            <select className={selectBase} value={form.initialStatus} onChange={(e) => setzen('initialStatus', e.target.value)}>
              {PROJECT_INITIAL_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {PROJECT_STATUS_LABELS[s].label}
                </option>
              ))}
            </select>
          </div>

          <div className={`${fieldWrapper} sm:col-span-2`}>
            <label className={fieldLabel}>Bezeichnung</label>
            <input className={inputBase} value={form.name} autoFocus onChange={(e) => setzen('name', e.target.value)} />
          </div>

          <div className={`${fieldWrapper} sm:col-span-2`}>
            <label className={fieldLabel}>Kunde {kundePflicht && <span className="text-destructive">*</span>}</label>
            <KundenSuche wert={kunde} onWahl={setKunde} />
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button type="button" className="rounded-md border border-border px-4 py-2 text-sm" onClick={onClose}>
            Abbrechen
          </button>
          <button
            type="button"
            className="rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground disabled:opacity-50"
            disabled={speichert || form.name.trim().length === 0}
            onClick={() => void anlegen()}
          >
            {speichert ? 'Legt an …' : 'Anlegen'}
          </button>
        </div>
      </div>
    </div>
  )
}
